import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry-specific design configurations
const INDUSTRY_DESIGNS = {
  'Construction': {
    icon: '🏗️',
    designTypes: [
      { id: 'project_showcase', name: 'Project Showcase', icon: '🏢', desc: 'Completed projects, before/after' },
      { id: 'safety_announcement', name: 'Safety Notice', icon: '⚠️', desc: 'Safety guidelines, PPE reminders' },
      { id: 'hiring', name: 'Now Hiring', icon: '👷', desc: 'Job openings, recruitment' },
      { id: 'service_promo', name: 'Services', icon: '🔨', desc: 'Construction services promotion' },
      { id: 'event_banner', name: 'Event Banner', icon: '🎪', desc: 'Trade shows, exhibitions' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'project', name: 'Project Complete', type: 'project_showcase', prompt: 'Professional construction project completion banner with modern building imagery', title: 'PROJECT COMPLETED', subtitle: 'On Time, On Budget', cta: 'View Portfolio', style: 'corporate', colorScheme: 'dark' },
      { id: 'safety', name: 'Safety First', type: 'safety_announcement', prompt: 'Construction safety awareness banner with protective equipment', title: 'SAFETY FIRST', subtitle: 'Your safety is our priority', cta: 'Learn More', style: 'bold', colorScheme: 'auto' },
      { id: 'hiring', name: 'Join Our Team', type: 'hiring', prompt: 'Construction company recruitment banner', title: 'JOIN OUR TEAM', subtitle: 'Building careers together', cta: 'Apply Now', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Digital Marketing': {
    icon: '📊',
    designTypes: [
      { id: 'case_study', name: 'Case Study', icon: '📈', desc: 'Success stories, ROI results' },
      { id: 'service_promo', name: 'Services', icon: '🎯', desc: 'SEO, PPC, Social Media services' },
      { id: 'webinar', name: 'Webinar', icon: '💻', desc: 'Marketing webinars, workshops' },
      { id: 'tips', name: 'Marketing Tips', icon: '💡', desc: 'Tips, tricks, insights' },
      { id: 'social_ad', name: 'Social Ad', icon: '📱', desc: 'Ad campaigns, promotions' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'growth', name: '10X Growth', type: 'case_study', prompt: 'Digital marketing success case study with growth charts', title: '10X GROWTH', subtitle: 'Real results for real businesses', cta: 'See How', style: 'modern', colorScheme: 'dark' },
      { id: 'seo', name: 'SEO Services', type: 'service_promo', prompt: 'Professional SEO services banner with analytics elements', title: 'RANK #1', subtitle: 'Dominate search results', cta: 'Get Started', style: 'bold', colorScheme: 'brand' },
      { id: 'webinar', name: 'Free Masterclass', type: 'webinar', prompt: 'Digital marketing masterclass webinar banner', title: 'FREE MASTERCLASS', subtitle: 'Learn from industry experts', cta: 'Register Free', style: 'elegant', colorScheme: 'dark' }
    ]
  },
  'Medical': {
    icon: '🏥',
    designTypes: [
      { id: 'health_awareness', name: 'Health Awareness', icon: '❤️', desc: 'Health tips, disease prevention' },
      { id: 'service_promo', name: 'Medical Services', icon: '🩺', desc: 'Treatments, procedures' },
      { id: 'doctor_intro', name: 'Meet Our Doctors', icon: '👨‍⚕️', desc: 'Doctor profiles, specializations' },
      { id: 'appointment', name: 'Book Appointment', icon: '📅', desc: 'Appointment booking promos' },
      { id: 'event_banner', name: 'Health Camp', icon: '🎪', desc: 'Health camps, free checkups' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'checkup', name: 'Free Health Checkup', type: 'event_banner', prompt: 'Medical health camp banner with caring healthcare imagery', title: 'FREE HEALTH CHECKUP', subtitle: 'Your health matters to us', cta: 'Book Now', style: 'modern', colorScheme: 'light' },
      { id: 'awareness', name: 'Heart Health', type: 'health_awareness', prompt: 'Heart health awareness campaign banner', title: 'HEART HEALTH MONTH', subtitle: 'Take care of your heart', cta: 'Learn More', style: 'elegant', colorScheme: 'auto' },
      { id: 'doctor', name: 'Expert Care', type: 'doctor_intro', prompt: 'Professional medical team introduction banner', title: 'EXPERT CARE', subtitle: 'Trusted by thousands', cta: 'Meet Our Team', style: 'corporate', colorScheme: 'brand' }
    ]
  },
  'Retail': {
    icon: '🛒',
    designTypes: [
      { id: 'sale', name: 'Sale Banner', icon: '🏷️', desc: 'Sales, discounts, clearance' },
      { id: 'new_arrival', name: 'New Arrivals', icon: '✨', desc: 'New products, collections' },
      { id: 'seasonal', name: 'Seasonal Promo', icon: '🎄', desc: 'Holiday, seasonal offers' },
      { id: 'loyalty', name: 'Loyalty Program', icon: '⭐', desc: 'Rewards, membership' },
      { id: 'store_event', name: 'Store Event', icon: '🎉', desc: 'Grand opening, events' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'mega_sale', name: 'Mega Sale', type: 'sale', prompt: 'Eye-catching retail mega sale banner with shopping elements', title: 'MEGA SALE', subtitle: 'Up to 70% OFF Everything', cta: 'Shop Now', style: 'bold', colorScheme: 'auto' },
      { id: 'new', name: 'Just Arrived', type: 'new_arrival', prompt: 'New collection arrival announcement banner', title: 'JUST ARRIVED', subtitle: 'Fresh new styles', cta: 'Explore', style: 'elegant', colorScheme: 'light' },
      { id: 'opening', name: 'Grand Opening', type: 'store_event', prompt: 'Grand store opening celebration banner', title: 'GRAND OPENING', subtitle: 'Special launch offers', cta: 'Visit Us', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Technology': {
    icon: '💻',
    designTypes: [
      { id: 'product_launch', name: 'Product Launch', icon: '🚀', desc: 'New tech products, features' },
      { id: 'tech_event', name: 'Tech Event', icon: '🎪', desc: 'Conferences, hackathons' },
      { id: 'saas_promo', name: 'SaaS Promo', icon: '☁️', desc: 'Software promotions' },
      { id: 'hiring', name: 'Tech Hiring', icon: '👨‍💻', desc: 'Developer jobs, IT roles' },
      { id: 'case_study', name: 'Case Study', icon: '📊', desc: 'Success stories, implementations' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'launch', name: 'Product Launch', type: 'product_launch', prompt: 'Futuristic tech product launch banner with modern UI elements', title: 'INTRODUCING', subtitle: 'The future is here', cta: 'Learn More', style: 'modern', colorScheme: 'dark' },
      { id: 'conference', name: 'Tech Summit', type: 'tech_event', prompt: 'Technology conference banner with futuristic elements', title: 'TECH SUMMIT 2026', subtitle: 'Innovation meets excellence', cta: 'Register Now', style: 'modern', colorScheme: 'dark' },
      { id: 'saas', name: 'Try Free', type: 'saas_promo', prompt: 'SaaS product free trial promotion banner', title: 'TRY FREE', subtitle: '14-day free trial', cta: 'Start Free', style: 'bold', colorScheme: 'brand' }
    ]
  },
  'Real Estate': {
    icon: '🏠',
    designTypes: [
      { id: 'property_listing', name: 'Property Listing', icon: '🏡', desc: 'Homes, apartments for sale/rent' },
      { id: 'open_house', name: 'Open House', icon: '🚪', desc: 'Open house events' },
      { id: 'sold', name: 'Just Sold', icon: '🎉', desc: 'Sold properties, success' },
      { id: 'agent_promo', name: 'Agent Profile', icon: '👔', desc: 'Agent introduction, expertise' },
      { id: 'market_update', name: 'Market Update', icon: '📈', desc: 'Market trends, insights' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'luxury', name: 'Luxury Home', type: 'property_listing', prompt: 'Luxury real estate property listing banner with elegant design', title: 'LUXURY LIVING', subtitle: 'Your dream home awaits', cta: 'Schedule Tour', style: 'elegant', colorScheme: 'dark' },
      { id: 'open', name: 'Open House', type: 'open_house', prompt: 'Real estate open house event banner', title: 'OPEN HOUSE', subtitle: 'This Sunday 2-5 PM', cta: 'RSVP Now', style: 'modern', colorScheme: 'brand' },
      { id: 'sold', name: 'Just Sold', type: 'sold', prompt: 'Real estate just sold celebration banner', title: 'JUST SOLD!', subtitle: 'Another happy family', cta: 'List With Us', style: 'bold', colorScheme: 'auto' }
    ]
  },
  'Finance': {
    icon: '💰',
    designTypes: [
      { id: 'investment', name: 'Investment', icon: '📈', desc: 'Investment opportunities' },
      { id: 'loan_promo', name: 'Loan Offer', icon: '🏦', desc: 'Loans, mortgages, credit' },
      { id: 'tax_service', name: 'Tax Services', icon: '📋', desc: 'Tax preparation, planning' },
      { id: 'seminar', name: 'Financial Seminar', icon: '🎓', desc: 'Workshops, education' },
      { id: 'consultation', name: 'Free Consultation', icon: '💼', desc: 'Advisory services' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'invest', name: 'Start Investing', type: 'investment', prompt: 'Professional investment opportunity banner with growth charts', title: 'GROW YOUR WEALTH', subtitle: 'Smart investing made simple', cta: 'Get Started', style: 'corporate', colorScheme: 'dark' },
      { id: 'loan', name: 'Low Rate Loan', type: 'loan_promo', prompt: 'Bank loan promotion banner with trust elements', title: 'LOWEST RATES', subtitle: 'Quick approval, easy process', cta: 'Apply Now', style: 'modern', colorScheme: 'brand' },
      { id: 'tax', name: 'Tax Season', type: 'tax_service', prompt: 'Tax preparation service banner', title: 'TAX SEASON', subtitle: 'Maximize your returns', cta: 'Book Now', style: 'bold', colorScheme: 'auto' }
    ]
  },
  'Education': {
    icon: '📚',
    designTypes: [
      { id: 'admission', name: 'Admissions Open', icon: '🎓', desc: 'Enrollment, admissions' },
      { id: 'course_promo', name: 'Course Promo', icon: '📖', desc: 'Courses, programs' },
      { id: 'webinar', name: 'Educational Webinar', icon: '💻', desc: 'Online sessions, workshops' },
      { id: 'scholarship', name: 'Scholarship', icon: '🏆', desc: 'Scholarships, grants' },
      { id: 'event', name: 'School Event', icon: '🎪', desc: 'Events, competitions' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'admission', name: 'Admissions Open', type: 'admission', prompt: 'Educational institution admissions banner with academic elements', title: 'ADMISSIONS OPEN', subtitle: 'Shape your future', cta: 'Apply Now', style: 'modern', colorScheme: 'brand' },
      { id: 'course', name: 'New Course', type: 'course_promo', prompt: 'Online course promotion banner', title: 'LEARN NEW SKILLS', subtitle: 'Expert-led courses', cta: 'Enroll Now', style: 'bold', colorScheme: 'dark' },
      { id: 'scholarship', name: 'Scholarship', type: 'scholarship', prompt: 'Scholarship opportunity announcement banner', title: 'SCHOLARSHIP', subtitle: '100% tuition coverage', cta: 'Apply Today', style: 'elegant', colorScheme: 'auto' }
    ]
  },
  'Food & Restaurant': {
    icon: '🍽️',
    designTypes: [
      { id: 'menu_special', name: 'Menu Special', icon: '🍔', desc: 'Daily specials, new dishes' },
      { id: 'offer', name: 'Food Offer', icon: '🎁', desc: 'Discounts, combos, deals' },
      { id: 'event', name: 'Food Event', icon: '🎉', desc: 'Food festivals, tastings' },
      { id: 'delivery', name: 'Delivery Promo', icon: '🚗', desc: 'Delivery offers, free delivery' },
      { id: 'opening', name: 'Grand Opening', icon: '🎊', desc: 'New restaurant opening' },
      { id: 'menu_page', name: 'Menu Page', icon: '📜', desc: 'Restaurant menu page with items, prices & descriptions' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'special', name: "Chef's Special", type: 'menu_special', prompt: 'Restaurant menu special banner with appetizing food photography', title: "CHEF'S SPECIAL", subtitle: 'Limited time only', cta: 'Order Now', style: 'elegant', colorScheme: 'dark' },
      { id: 'combo', name: 'Combo Deal', type: 'offer', prompt: 'Food combo deal promotional banner', title: 'COMBO DEAL', subtitle: 'Save 30% on meals', cta: 'Grab Deal', style: 'bold', colorScheme: 'auto' },
      { id: 'delivery', name: 'Free Delivery', type: 'delivery', prompt: 'Food delivery promotion banner', title: 'FREE DELIVERY', subtitle: 'On orders above $25', cta: 'Order Now', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Fashion & Beauty': {
    icon: '👗',
    designTypes: [
      { id: 'collection', name: 'New Collection', icon: '✨', desc: 'Fashion collections, trends' },
      { id: 'sale', name: 'Fashion Sale', icon: '🏷️', desc: 'Sales, clearance' },
      { id: 'beauty_promo', name: 'Beauty Promo', icon: '💄', desc: 'Beauty products, treatments' },
      { id: 'lookbook', name: 'Lookbook', icon: '📸', desc: 'Style inspiration, looks' },
      { id: 'event', name: 'Fashion Event', icon: '🎪', desc: 'Fashion shows, launches' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'collection', name: 'New Season', type: 'collection', prompt: 'Fashion new collection launch banner with elegant styling', title: 'NEW COLLECTION', subtitle: 'Spring/Summer 2026', cta: 'Shop Now', style: 'elegant', colorScheme: 'light' },
      { id: 'sale', name: 'End of Season', type: 'sale', prompt: 'Fashion end of season sale banner', title: 'END OF SEASON', subtitle: 'Up to 60% OFF', cta: 'Shop Sale', style: 'bold', colorScheme: 'auto' },
      { id: 'beauty', name: 'Beauty Box', type: 'beauty_promo', prompt: 'Beauty products subscription box promotion', title: 'BEAUTY BOX', subtitle: 'Curated just for you', cta: 'Subscribe', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Facilities Management': {
    icon: '🏢',
    designTypes: [
      { id: 'service', name: 'Services', icon: '🔧', desc: 'Maintenance, cleaning services' },
      { id: 'contract', name: 'Contract Offer', icon: '📝', desc: 'Annual contracts, packages' },
      { id: 'emergency', name: 'Emergency Service', icon: '🚨', desc: '24/7 emergency services' },
      { id: 'hiring', name: 'Now Hiring', icon: '👷', desc: 'Job openings' },
      { id: 'case_study', name: 'Success Story', icon: '🏆', desc: 'Client testimonials' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'service', name: 'Full Service', type: 'service', prompt: 'Facilities management services banner with professional imagery', title: 'FULL SERVICE', subtitle: 'One partner for all needs', cta: 'Get Quote', style: 'corporate', colorScheme: 'brand' },
      { id: 'emergency', name: '24/7 Support', type: 'emergency', prompt: 'Emergency facilities service banner', title: '24/7 EMERGENCY', subtitle: 'Always here for you', cta: 'Call Now', style: 'bold', colorScheme: 'dark' },
      { id: 'contract', name: 'Annual Plan', type: 'contract', prompt: 'Annual facilities management contract banner', title: 'ANNUAL PLAN', subtitle: 'Save 20% on yearly contract', cta: 'Learn More', style: 'modern', colorScheme: 'auto' }
    ]
  },
  'Travel & Hospitality': {
    icon: '✈️',
    designTypes: [
      { id: 'destination', name: 'Destination', icon: '🏝️', desc: 'Travel destinations' },
      { id: 'package', name: 'Travel Package', icon: '🎒', desc: 'Tour packages, deals' },
      { id: 'hotel', name: 'Hotel Promo', icon: '🏨', desc: 'Hotel offers, bookings' },
      { id: 'seasonal', name: 'Seasonal Offer', icon: '🌴', desc: 'Holiday specials' },
      { id: 'event', name: 'Travel Event', icon: '🎪', desc: 'Travel fairs, expos' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'escape', name: 'Dream Escape', type: 'destination', prompt: 'Tropical travel destination banner with paradise imagery', title: 'DREAM ESCAPE', subtitle: 'Your paradise awaits', cta: 'Book Now', style: 'elegant', colorScheme: 'auto' },
      { id: 'package', name: 'All Inclusive', type: 'package', prompt: 'All inclusive travel package promotion banner', title: 'ALL INCLUSIVE', subtitle: 'From $999 per person', cta: 'View Packages', style: 'bold', colorScheme: 'dark' },
      { id: 'hotel', name: 'Luxury Stay', type: 'hotel', prompt: 'Luxury hotel promotion banner', title: 'LUXURY STAY', subtitle: 'Experience 5-star comfort', cta: 'Reserve', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Fitness & Wellness': {
    icon: '💪',
    designTypes: [
      { id: 'membership', name: 'Membership', icon: '🎫', desc: 'Gym memberships, plans' },
      { id: 'class', name: 'Fitness Class', icon: '🧘', desc: 'Yoga, HIIT, classes' },
      { id: 'challenge', name: 'Fitness Challenge', icon: '🏆', desc: 'Challenges, competitions' },
      { id: 'wellness', name: 'Wellness Program', icon: '🌿', desc: 'Wellness, nutrition' },
      { id: 'trainer', name: 'Personal Training', icon: '🏋️', desc: 'PT sessions, coaching' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'join', name: 'Join Now', type: 'membership', prompt: 'Gym membership promotion banner with fitness motivation', title: 'TRANSFORM', subtitle: 'Start your fitness journey', cta: 'Join Now', style: 'bold', colorScheme: 'dark' },
      { id: 'challenge', name: '30 Day Challenge', type: 'challenge', prompt: 'Fitness challenge promotional banner', title: '30 DAY CHALLENGE', subtitle: 'Change your life', cta: 'Sign Up', style: 'modern', colorScheme: 'brand' },
      { id: 'yoga', name: 'Yoga Classes', type: 'class', prompt: 'Yoga class promotion banner with peaceful imagery', title: 'FIND YOUR ZEN', subtitle: 'Yoga for all levels', cta: 'Book Class', style: 'elegant', colorScheme: 'light' }
    ]
  },
  'Legal Services': {
    icon: '⚖️',
    designTypes: [
      { id: 'consultation', name: 'Free Consultation', icon: '💼', desc: 'Free legal consultations' },
      { id: 'service', name: 'Legal Services', icon: '📜', desc: 'Practice areas' },
      { id: 'case_result', name: 'Case Results', icon: '🏆', desc: 'Victories, settlements' },
      { id: 'seminar', name: 'Legal Seminar', icon: '🎓', desc: 'Workshops, education' },
      { id: 'attorney', name: 'Attorney Profile', icon: '👔', desc: 'Attorney introductions' }
    ],
    templates: [
      { id: 'consult', name: 'Free Consultation', type: 'consultation', prompt: 'Law firm free consultation banner with professional imagery', title: 'FREE CONSULTATION', subtitle: 'Know your rights', cta: 'Book Now', style: 'corporate', colorScheme: 'dark' },
      { id: 'wins', name: 'We Fight For You', type: 'case_result', prompt: 'Legal victory results banner', title: '$10M+ RECOVERED', subtitle: 'We fight for your rights', cta: 'Contact Us', style: 'bold', colorScheme: 'brand' },
      { id: 'service', name: 'Expert Legal', type: 'service', prompt: 'Legal services overview banner', title: 'EXPERT LEGAL', subtitle: '25+ years of experience', cta: 'Learn More', style: 'elegant', colorScheme: 'auto' }
    ]
  },
  'E-commerce': {
    icon: '🛍️',
    designTypes: [
      { id: 'flash_sale', name: 'Flash Sale', icon: '⚡', desc: 'Limited time offers' },
      { id: 'new_product', name: 'New Product', icon: '✨', desc: 'Product launches' },
      { id: 'free_shipping', name: 'Free Shipping', icon: '🚚', desc: 'Shipping promotions' },
      { id: 'bundle', name: 'Bundle Deal', icon: '📦', desc: 'Product bundles' },
      { id: 'loyalty', name: 'Rewards', icon: '⭐', desc: 'Loyalty programs' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'flash', name: 'Flash Sale', type: 'flash_sale', prompt: 'E-commerce flash sale banner with urgency elements', title: 'FLASH SALE', subtitle: 'Ends in 24 hours!', cta: 'Shop Now', style: 'bold', colorScheme: 'auto' },
      { id: 'shipping', name: 'Free Shipping', type: 'free_shipping', prompt: 'Free shipping promotion banner', title: 'FREE SHIPPING', subtitle: 'On all orders today', cta: 'Shop Now', style: 'modern', colorScheme: 'brand' },
      { id: 'bundle', name: 'Bundle & Save', type: 'bundle', prompt: 'Product bundle deal banner', title: 'BUNDLE & SAVE', subtitle: 'Buy more, save more', cta: 'View Bundles', style: 'elegant', colorScheme: 'dark' }
    ]
  },
  'Automotive': {
    icon: '🚗',
    designTypes: [
      { id: 'new_model', name: 'New Model', icon: '🚙', desc: 'New car launches' },
      { id: 'sale_event', name: 'Sales Event', icon: '🎉', desc: 'Clearance, deals' },
      { id: 'service', name: 'Auto Service', icon: '🔧', desc: 'Maintenance, repairs' },
      { id: 'test_drive', name: 'Test Drive', icon: '🏎️', desc: 'Test drive invitations' },
      { id: 'financing', name: 'Financing', icon: '💳', desc: 'Loan, lease offers' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'launch', name: 'New Arrival', type: 'new_model', prompt: 'Automotive new car launch banner with sleek design', title: 'ALL NEW 2026', subtitle: 'Redefining performance', cta: 'Explore', style: 'elegant', colorScheme: 'dark' },
      { id: 'sale', name: 'Year End Sale', type: 'sale_event', prompt: 'Auto dealership year end sale banner', title: 'YEAR END SALE', subtitle: 'Huge savings on all models', cta: 'View Deals', style: 'bold', colorScheme: 'auto' },
      { id: 'service', name: 'Service Special', type: 'service', prompt: 'Auto service promotion banner', title: 'SERVICE SPECIAL', subtitle: 'Oil change from $29.99', cta: 'Book Now', style: 'modern', colorScheme: 'brand' }
    ]
  },
  'Entertainment': {
    icon: '🎬',
    designTypes: [
      { id: 'event', name: 'Event Promo', icon: '🎭', desc: 'Shows, concerts, events' },
      { id: 'movie', name: 'Movie Release', icon: '🎬', desc: 'Film releases, premieres' },
      { id: 'streaming', name: 'Streaming', icon: '📺', desc: 'Streaming content' },
      { id: 'gaming', name: 'Gaming', icon: '🎮', desc: 'Games, esports' },
      { id: 'festival', name: 'Festival', icon: '🎪', desc: 'Festivals, carnivals' },
      { id: 'poster', name: 'Poster', icon: '📋', desc: 'Large format poster, all fields optional' }
    ],
    templates: [
      { id: 'concert', name: 'Live Concert', type: 'event', prompt: 'Live concert event promotional banner with vibrant energy', title: 'LIVE IN CONCERT', subtitle: 'One night only', cta: 'Get Tickets', style: 'bold', colorScheme: 'dark' },
      { id: 'movie', name: 'Now Showing', type: 'movie', prompt: 'Movie release promotional banner', title: 'NOW SHOWING', subtitle: 'The movie everyone is talking about', cta: 'Book Tickets', style: 'modern', colorScheme: 'auto' },
      { id: 'festival', name: 'Music Festival', type: 'festival', prompt: 'Music festival promotional banner with colorful design', title: 'SUMMER FEST', subtitle: '3 days of music', cta: 'Buy Passes', style: 'creative', colorScheme: 'brand' }
    ]
  }
};

// Style options (same for all industries)
const DESIGN_STYLES = [
  { id: 'modern', name: 'Modern', desc: 'Clean, geometric' },
  { id: 'minimal', name: 'Minimal', desc: 'Simple, spacious' },
  { id: 'bold', name: 'Bold', desc: 'High impact' },
  { id: 'elegant', name: 'Elegant', desc: 'Sophisticated' },
  { id: 'corporate', name: 'Corporate', desc: 'Professional' },
  { id: 'creative', name: 'Creative', desc: 'Artistic, unique' }
];

// Color scheme options
const COLOR_SCHEMES = [
  { id: 'auto', name: 'Auto', color: 'linear-gradient(135deg, #667eea, #764ba2)' },
  { id: 'dark', name: 'Dark', color: '#1a1a2e' },
  { id: 'light', name: 'Light', color: '#f8fafc' },
  { id: 'brand', name: 'Brand', color: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }
];

// Aspect ratio options - organized by platform
const ASPECT_RATIOS = [
  // General
  { id: 'square', name: 'Square (1:1)', icon: '■', size: '1080×1080', category: 'general' },
  { id: 'landscape', name: 'Landscape (16:9)', icon: '▬', size: '1920×1080', category: 'general' },
  { id: 'portrait', name: 'Portrait (4:5)', icon: '▮', size: '1080×1350', category: 'general' },
  
  // Instagram
  { id: 'ig_post', name: 'Instagram Post', icon: '📸', size: '1080×1080', category: 'instagram' },
  { id: 'ig_story', name: 'Instagram Story', icon: '📱', size: '1080×1920', category: 'instagram' },
  { id: 'ig_portrait', name: 'Instagram Portrait', icon: '🖼️', size: '1080×1350', category: 'instagram' },
  { id: 'ig_dp', name: 'Instagram DP', icon: '👤', size: '400×400', category: 'instagram' },
  
  // Facebook
  { id: 'fb_post', name: 'Facebook Post', icon: '📝', size: '1200×630', category: 'facebook' },
  { id: 'fb_cover', name: 'Facebook Cover', icon: '🖼️', size: '1640×624', category: 'facebook' },
  { id: 'fb_story', name: 'Facebook Story', icon: '📱', size: '1080×1920', category: 'facebook' },
  { id: 'fb_dp', name: 'Facebook DP', icon: '👤', size: '400×400', category: 'facebook' },
  
  // TikTok
  { id: 'tiktok', name: 'TikTok Video', icon: '🎵', size: '1080×1920', category: 'tiktok' },
  { id: 'tiktok_dp', name: 'TikTok DP', icon: '👤', size: '400×400', category: 'tiktok' },
  
  // LinkedIn
  { id: 'linkedin_post', name: 'LinkedIn Post', icon: '💼', size: '1200×627', category: 'linkedin' },
  { id: 'linkedin_cover', name: 'LinkedIn Cover', icon: '🏢', size: '1584×396', category: 'linkedin' },
  { id: 'linkedin_dp', name: 'LinkedIn DP', icon: '👤', size: '400×400', category: 'linkedin' },
  
  // Twitter/X
  { id: 'twitter_post', name: 'Twitter/X Post', icon: '🐦', size: '1200×675', category: 'twitter' },
  { id: 'twitter_header', name: 'Twitter/X Header', icon: '🔷', size: '1500×500', category: 'twitter' },
  { id: 'twitter_dp', name: 'Twitter/X DP', icon: '👤', size: '400×400', category: 'twitter' },
  
  // YouTube
  { id: 'youtube_thumb', name: 'YouTube Thumbnail', icon: '▶️', size: '1280×720', category: 'youtube' },
  { id: 'youtube_banner', name: 'YouTube Banner', icon: '📺', size: '2560×1440', category: 'youtube' },
  { id: 'youtube_dp', name: 'YouTube DP', icon: '👤', size: '400×400', category: 'youtube' },
  
  // Display Ads / Banners
  { id: 'display_leaderboard', name: 'Leaderboard', icon: '📊', size: '728×90', category: 'display' },
  { id: 'display_banner', name: 'Banner', icon: '🏷️', size: '468×60', category: 'display' },
  { id: 'display_medium_rect', name: 'Medium Rectangle', icon: '▢', size: '300×250', category: 'display' },
  { id: 'display_large_rect', name: 'Large Rectangle', icon: '⬜', size: '336×280', category: 'display' },
  { id: 'display_skyscraper', name: 'Skyscraper', icon: '🏙️', size: '160×600', category: 'display' },
  { id: 'display_wide_sky', name: 'Wide Skyscraper', icon: '🏢', size: '300×600', category: 'display' },
  { id: 'display_billboard', name: 'Billboard', icon: '📋', size: '970×250', category: 'display' },
  { id: 'display_mobile', name: 'Mobile Banner', icon: '📱', size: '320×50', category: 'display' },
  { id: 'display_mobile_lg', name: 'Mobile Leaderboard', icon: '📲', size: '320×100', category: 'display' },

  // Large Format / Print
  { id: 'print_60x80', name: '60×80 inch', icon: '🖼️', size: '1080×1440', category: 'print', inches: '60"×80"' },
  { id: 'print_24x36', name: '24×36 inch', icon: '📄', size: '960×1440', category: 'print', inches: '24"×36"' },
  { id: 'print_36x48', name: '36×48 inch', icon: '🗞️', size: '1080×1440', category: 'print', inches: '36"×48"' },
  { id: 'print_48x72', name: '48×72 inch', icon: '🏗️', size: '960×1440', category: 'print', inches: '48"×72"' },
  { id: 'print_a3', name: 'A3 (11.7×16.5)', icon: '📃', size: '1014×1440', category: 'print', inches: '11.7"×16.5"' },
  { id: 'print_a2', name: 'A2 (16.5×23.4)', icon: '📜', size: '1016×1440', category: 'print', inches: '16.5"×23.4"' },
  { id: 'print_a1', name: 'A1 (23.4×33.1)', icon: '🗺️', size: '1018×1440', category: 'print', inches: '23.4"×33.1"' },
];

const BORDER_STYLES = [
  { id: 'none', name: 'No Border', icon: '⬜', desc: '' },
  { id: 'solid', name: 'Solid Border', icon: '🔲', desc: 'Clean solid border around the design' },
  { id: 'double', name: 'Double Line', icon: '🔳', desc: 'Double-line border with inner and outer frame' },
  { id: 'rounded', name: 'Rounded Frame', icon: '⭕', desc: 'Rounded corners with soft border' },
  { id: 'ornamental', name: 'Ornamental', icon: '🎨', desc: 'Decorative ornamental border with artistic corners and flourishes' },
  { id: 'geometric', name: 'Geometric Pattern', icon: '🔶', desc: 'Modern geometric pattern border with shapes and lines' },
  { id: 'gradient', name: 'Gradient Border', icon: '🌈', desc: 'Gradient color border fading between brand colors' },
  { id: 'luxury', name: 'Luxury Gold', icon: '👑', desc: 'Premium gold/metallic decorative border with elegant details' },
];

function AIDesignGenerator({ userId, industry = 'Technology', onDesignGenerated, onClose }) {
  const [step, setStep] = useState(1); // 1: Type, 2: Details, 3: Style, 4: Result, 5: AI Content, 6: Post
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  
  // AI Content state
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [aiCaption, setAiCaption] = useState('');
  const [aiHashtags, setAiHashtags] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('instagram');
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [postSaved, setPostSaved] = useState(false);
  
  // Get industry-specific config
  const industryConfig = useMemo(() => {
    return INDUSTRY_DESIGNS[industry] || INDUSTRY_DESIGNS['Technology'];
  }, [industry]);
  
  // Form state
  const [designType, setDesignType] = useState(industryConfig.designTypes[0]?.id || 'event_banner');
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [dateText, setDateText] = useState('');
  const [locationText, setLocationText] = useState('');
  const [style, setStyle] = useState('modern');
  const [colorScheme, setColorScheme] = useState('auto');
  const [aspectRatio, setAspectRatio] = useState('landscape');
  const [includeLogo, setIncludeLogo] = useState(true);
  
  // Border settings
  const [borderStyle, setBorderStyle] = useState('none');
  const [borderColor, setBorderColor] = useState('');
  
  // Menu page state (for design_type === 'menu_page')
  // menuPages: array of { section, items: [...] } — one per page
  const [menuPages, setMenuPages] = useState([
    { section: '', items: [{ name: '', name_ar: '', description: '', description_ar: '', price: '' }] }
  ]);
  const [activePageIdx, setActivePageIdx] = useState(0);
  const [menuLanguages, setMenuLanguages] = useState(['en']); // ['en'], ['ar'], or ['en','ar']
  const [menuRenderMode, setMenuRenderMode] = useState('artistic'); // 'artistic' | 'precision'
  // Restaurant-wide footer fields (same across all pages)
  const [menuContact, setMenuContact] = useState('');
  const [menuLocation, setMenuLocation] = useState('');
  const [menuTagline, setMenuTagline] = useState('');
  const [generatedPages, setGeneratedPages] = useState([]); // [{pageNumber, image}]
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  const isMenu = designType === 'menu_page';
  const isBilingual = menuLanguages.includes('en') && menuLanguages.includes('ar');
  const isArabicOnly = menuLanguages.length === 1 && menuLanguages[0] === 'ar';

  // Derived: current active page
  const currentPage = menuPages[activePageIdx] || menuPages[0];
  const menuItems = currentPage?.items || [];
  const menuSection = currentPage?.section || '';
  const menuTotalPages = menuPages.length;
  const menuPageNumber = activePageIdx + 1;

  // Update fields on the active page
  const updateActivePage = (patch) => {
    setMenuPages(prev => prev.map((p, i) => i === activePageIdx ? { ...p, ...patch } : p));
  };
  const setMenuSection = (val) => updateActivePage({ section: val });

  const addPage = () => {
    const newPage = { section: '', items: [{ name: '', name_ar: '', description: '', description_ar: '', price: '' }] };
    setMenuPages([...menuPages, newPage]);
    setActivePageIdx(menuPages.length); // jump to new page
  };
  const removePage = (idx) => {
    if (menuPages.length === 1) return;
    const next = menuPages.filter((_, i) => i !== idx);
    setMenuPages(next);
    if (activePageIdx >= next.length) setActivePageIdx(next.length - 1);
  };
  const duplicatePage = (idx) => {
    const src = menuPages[idx];
    const copy = JSON.parse(JSON.stringify(src));
    const next = [...menuPages.slice(0, idx + 1), copy, ...menuPages.slice(idx + 1)];
    setMenuPages(next);
    setActivePageIdx(idx + 1);
  };

  const addMenuItem = () => {
    updateActivePage({ items: [...menuItems, { name: '', name_ar: '', description: '', description_ar: '', price: '' }] });
  };
  const updateMenuItem = (idx, field, value) => {
    const nextItems = menuItems.map((it, i) => i === idx ? { ...it, [field]: value } : it);
    updateActivePage({ items: nextItems });
  };
  const removeMenuItem = (idx) => {
    if (menuItems.length === 1) return;
    updateActivePage({ items: menuItems.filter((_, i) => i !== idx) });
  };
  const setMenuItems = (items) => updateActivePage({ items });

  const toggleMenuLanguage = (lang) => {
    if (menuLanguages.includes(lang)) {
      if (menuLanguages.length === 1) return;
      setMenuLanguages(menuLanguages.filter(l => l !== lang));
    } else {
      setMenuLanguages([...menuLanguages, lang]);
    }
  };

  // Bulk paste support
  const [bulkPasteMode, setBulkPasteMode] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState('');

  const parseBulkPaste = () => {
    const text = bulkPasteText.trim();
    if (!text) return;
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const isBi = menuLanguages.includes('en') && menuLanguages.includes('ar');
    const isAr = menuLanguages.length === 1 && menuLanguages[0] === 'ar';

    const parsed = lines.map(line => {
      // Split by | or tab. Trim each part.
      const parts = line.split(/\s*[|\t]\s*/).map(p => p.trim());
      // parts: [name, description, price] OR for bilingual: [en_name / ar_name, en_desc / ar_desc, price]
      const split = (s) => {
        if (!s) return ['', ''];
        // split by ' / ' or ' — ' (em dash) or '|' double, prefer ' / '
        const m = s.split(/\s*\/\s*/);
        return [m[0] || '', m.slice(1).join(' / ') || ''];
      };

      let name = '', name_ar = '', description = '', description_ar = '', price = '';
      const [p0, p1, p2] = parts;

      if (isBi) {
        const [en, ar] = split(p0 || '');
        name = en; name_ar = ar;
        const [enD, arD] = split(p1 || '');
        description = enD; description_ar = arD;
        price = p2 || '';
      } else if (isAr) {
        name_ar = p0 || '';
        description_ar = p1 || '';
        price = p2 || '';
      } else {
        name = p0 || '';
        description = p1 || '';
        price = p2 || '';
      }
      return { name, name_ar, description, description_ar, price };
    }).filter(it => it.name || it.name_ar);

    if (parsed.length === 0) return;
    setMenuItems(parsed);
    setBulkPasteText('');
    setBulkPasteMode(false);
  };
  
  // User brand settings (fetched from API)
  const [brandSettings, setBrandSettings] = useState(null);
  
  // Design Presets
  const [presets, setPresets] = useState([]);
  const [showPresetPanel, setShowPresetPanel] = useState(false);
  const [presetName, setPresetName] = useState('');
  
  // Fetch user brand settings and saved presets
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/users/${userId}`).then(res => {
      if (res.data) setBrandSettings(res.data);
    }).catch(() => {});
    // Load saved design presets from localStorage
    try {
      const saved = JSON.parse(localStorage.getItem(`design_presets_${userId}`) || '[]');
      setPresets(saved);
    } catch {}
  }, [userId]);
  
  // Custom size state
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [customUnit, setCustomUnit] = useState('inches');
  const [showCustomSize, setShowCustomSize] = useState(false);
  
  // Calculate proportional pixel dimensions from custom size
  const getCustomPixelSize = () => {
    let w = parseFloat(customWidth);
    let h = parseFloat(customHeight);
    if (!w || !h || w <= 0 || h <= 0) return null;
    // Convert to inches for consistent ratio calculation
    if (customUnit === 'feet') { w *= 12; h *= 12; }
    if (customUnit === 'cm') { w /= 2.54; h /= 2.54; }
    const maxDim = 1440;
    const scale = maxDim / Math.max(w, h);
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
  };

  // Save current selections as a design preset
  const savePreset = () => {
    if (!presetName.trim()) return;
    const preset = {
      id: Date.now(),
      name: presetName.trim(),
      style, colorScheme, borderStyle, borderColor,
      aspectRatio, showCustomSize, customWidth, customHeight, customUnit,
      includeLogo
    };
    const updated = [...presets, preset];
    setPresets(updated);
    localStorage.setItem(`design_presets_${userId}`, JSON.stringify(updated));
    setPresetName('');
    setShowPresetPanel(false);
  };

  const loadPreset = (preset) => {
    setStyle(preset.style || 'modern');
    setColorScheme(preset.colorScheme || 'auto');
    setBorderStyle(preset.borderStyle || 'none');
    setBorderColor(preset.borderColor || '');
    setAspectRatio(preset.aspectRatio || 'landscape');
    setShowCustomSize(preset.showCustomSize || false);
    if (preset.customWidth) setCustomWidth(preset.customWidth);
    if (preset.customHeight) setCustomHeight(preset.customHeight);
    if (preset.customUnit) setCustomUnit(preset.customUnit);
    setIncludeLogo(preset.includeLogo !== undefined ? preset.includeLogo : true);
  };

  const deletePreset = (id) => {
    const updated = presets.filter(p => p.id !== id);
    setPresets(updated);
    localStorage.setItem(`design_presets_${userId}`, JSON.stringify(updated));
  };

  // High-res download function
  const [downloadScale, setDownloadScale] = useState(2);
  
  const downloadHighRes = useCallback(() => {
    if (!generatedImage) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth * downloadScale;
      canvas.height = img.naturalHeight * downloadScale;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `${industry.toLowerCase().replace(/\s+/g, '-')}-design-${downloadScale}x.png`;
      link.href = dataURL;
      link.click();
    };
    img.src = generatedImage;
  }, [generatedImage, downloadScale, industry]);

  // Platform options
  const PLATFORMS = [
    { id: 'instagram', name: 'Instagram', icon: '📸' },
    { id: 'facebook', name: 'Facebook', icon: '👤' },
    { id: 'twitter', name: 'Twitter/X', icon: '🐦' },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵' }
  ];

  // Apply template
  const applyTemplate = useCallback((template) => {
    setDesignType(template.type);
    setPrompt(template.prompt);
    setTitle(template.title);
    setSubtitle(template.subtitle);
    setCtaText(template.cta);
    setStyle(template.style);
    setColorScheme(template.colorScheme);
    setStep(2);
  }, []);

  // Generate AI Caption
  const generateAIContent = async () => {
    setIsGeneratingContent(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/image/generate-ai-caption`, {
        user_id: userId,
        image_base64: generatedImage,
        platform: selectedPlatform,
        industry: industry,
        context: `${title} - ${subtitle}. ${prompt}`
      });
      
      if (response.data) {
        setAiCaption(response.data.caption || '');
        setAiHashtags(response.data.hashtags || '');
      }
    } catch (err) {
      console.error('Error generating AI content:', err);
      // Generate fallback content based on design details
      const fallbackCaption = `${title}\n\n${subtitle}\n\n${ctaText ? `👉 ${ctaText}` : ''}`;
      const fallbackHashtags = `#${industry.replace(/\s+/g, '')} #${designType} #marketing #design`;
      setAiCaption(fallbackCaption);
      setAiHashtags(fallbackHashtags);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  // Save as post
  const saveAsPost = async () => {
    setIsSavingPost(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/posts/create`, {
        user_id: userId,
        caption: aiCaption,
        hashtags: aiHashtags,
        image_url: generatedImage,
        platform: selectedPlatform,
        status: 'draft',
        industry: industry,
        design_type: designType
      });
      
      if (response.data) {
        setPostSaved(true);
        setStep(6);
      }
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post. Please try again.');
    } finally {
      setIsSavingPost(false);
    }
  };

  // Build payload for a specific menu page index (or non-menu single design)
  const buildDesignPayload = (pageIdxOverride = null) => {
    const effectivePrompt = isMenu
      ? (prompt.trim() || `Restaurant menu page, professional layout for printing`)
      : prompt;
    const payload = {
      user_id: userId,
      design_type: designType,
      prompt: `[${industry} Industry] ${effectivePrompt}`,
      title: title || null,
      subtitle: subtitle || null,
      cta_text: ctaText || null,
      date_text: dateText || null,
      location_text: locationText || null,
      color_scheme: colorScheme,
      aspect_ratio: showCustomSize ? 'custom' : aspectRatio,
      custom_size: showCustomSize ? getCustomPixelSize() : null,
      include_logo: includeLogo,
      style: style,
      border_style: borderStyle !== 'none' ? borderStyle : null,
      border_color: borderColor || null
    };

    if (isMenu) {
      const idx = pageIdxOverride != null ? pageIdxOverride : activePageIdx;
      const page = menuPages[idx] || menuPages[0];
      const cleanedItems = (page.items || [])
        .filter(it => (it.name || '').trim() || (it.name_ar || '').trim())
        .map(it => ({
          name: (it.name || '').trim(),
          name_ar: (it.name_ar || '').trim(),
          description: (it.description || '').trim(),
          description_ar: (it.description_ar || '').trim(),
          price: (it.price || '').trim()
        }));
      payload.menu_section = page.section || null;
      payload.menu_items = cleanedItems;
      payload.languages = menuLanguages;
      payload.page_number = idx + 1;
      payload.total_pages = menuPages.length;
      payload.menu_render_mode = menuRenderMode;
      payload.menu_contact = menuContact.trim() || null;
      payload.menu_location = menuLocation.trim() || null;
      payload.menu_tagline = menuTagline.trim() || null;
    }
    return payload;
  };

  // Generate design (single page or non-menu)
  const generateDesign = async () => {
    if (isMenu) {
      const hasItems = menuItems.some(it => (it.name || '').trim() || (it.name_ar || '').trim());
      if (!hasItems) {
        setError('Please add at least one menu item with a name on this page');
        return;
      }
    } else if (!prompt.trim()) {
      setError('Please describe what you want to create');
      return;
    }
    if (showCustomSize && !getCustomPixelSize()) {
      setError('Please enter valid width and height for custom size');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const payload = buildDesignPayload();
      // For menu mode: if we already generated Page 1 earlier, use it as the
      // brand reference image so this page matches exactly.
      if (isMenu && menuPageNumber > 1) {
        const page1 = generatedPages.find(p => p.pageNumber === 1);
        if (page1?.image) {
          payload.reference_image = page1.image;
        }
      }
      const response = await axios.post(`${API}/generate/design`, payload);

      if (response.data.success && response.data.image_base64) {
        const img = response.data.image_base64;
        setGeneratedImage(img);
        if (isMenu) {
          // Store in gallery, replacing any prior version of this page
          setGeneratedPages(prev => {
            const filtered = prev.filter(p => p.pageNumber !== menuPageNumber);
            return [...filtered, { pageNumber: menuPageNumber, section: menuSection, image: img }]
              .sort((a, b) => a.pageNumber - b.pageNumber);
          });
        }
        setStep(4);
      } else {
        setError('Failed to generate design');
      }
    } catch (err) {
      console.error('Design generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate design. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate ALL menu pages sequentially.
  // Page 1 is generated normally; Pages 2..N use Page 1 as a reference image
  // so Gemini matches the exact colors, fonts, borders and brand style.
  const generateAllPages = async () => {
    if (!isMenu) return;
    // Validate every page has at least one item
    for (let i = 0; i < menuPages.length; i++) {
      const has = (menuPages[i].items || []).some(it => (it.name || '').trim() || (it.name_ar || '').trim());
      if (!has) {
        setError(`Page ${i + 1} has no menu items. Please add items or remove the page.`);
        setActivePageIdx(i);
        return;
      }
    }
    setBulkGenerating(true);
    setError(null);
    setBulkProgress({ current: 0, total: menuPages.length });
    const results = [];
    let referenceImage = null; // populated after Page 1 succeeds
    try {
      for (let i = 0; i < menuPages.length; i++) {
        setBulkProgress({ current: i + 1, total: menuPages.length });
        const payload = buildDesignPayload(i);
        if (referenceImage && i > 0) {
          payload.reference_image = referenceImage;
        }
        const response = await axios.post(`${API}/generate/design`, payload);
        if (response.data.success && response.data.image_base64) {
          const img = response.data.image_base64;
          results.push({ pageNumber: i + 1, section: menuPages[i].section || '', image: img });
          // Lock Page 1 as the brand reference for all later pages
          if (i === 0) {
            referenceImage = img;
          }
        } else {
          throw new Error(`Page ${i + 1} failed to generate`);
        }
      }
      setGeneratedPages(results);
      setGeneratedImage(results[0]?.image || null);
      setStep(4);
    } catch (err) {
      console.error('Bulk generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Bulk generation failed.');
      // Save whatever succeeded
      if (results.length > 0) {
        setGeneratedPages(results);
      }
    } finally {
      setBulkGenerating(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  // Download a single page from gallery
  const downloadGeneratedPage = (page, scale = 2) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth * scale;
      canvas.height = img.naturalHeight * scale;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `menu-page-${page.pageNumber}${page.section ? '-' + page.section.toLowerCase().replace(/\s+/g, '-') : ''}-${scale}x.png`;
      link.href = dataURL;
      link.click();
    };
    img.src = page.image;
  };

  const downloadAllPages = (scale = 2) => {
    generatedPages.forEach((p, i) => {
      // Stagger downloads slightly so browser doesn't block
      setTimeout(() => downloadGeneratedPage(p, scale), i * 400);
    });
  };

  // Use generated design
  const useDesign = () => {
    if (generatedImage && onDesignGenerated) {
      onDesignGenerated(generatedImage);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div 
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{industryConfig.icon}</span>
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>AI Design Generator</h2>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Creating designs for <span className="font-semibold" style={{ color: 'var(--accent-from)' }}>{industry}</span>
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 transition"
            style={{ color: 'var(--text-secondary)' }}
          >
            ✕
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 p-4 border-b overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
          {['Type', 'Details', 'Style', 'Design', 'Content', 'Done'].map((label, i) => (
            <div key={i} className="flex items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-purple-500 text-white' : ''}`}
                style={step <= i ? { background: 'var(--bg-tertiary)', color: 'var(--text-muted)' } : {}}
              >
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span className="text-sm hidden sm:inline" style={{ color: step === i + 1 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
              {i < 3 && <div className="w-8 h-0.5" style={{ background: step > i + 1 ? '#22c55e' : 'var(--border-color)' }} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Choose Type */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Choose Design Type for {industry}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {industryConfig.designTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setDesignType(type.id)}
                      className={`p-4 rounded-xl text-center transition ${designType === type.id ? 'ring-2 ring-purple-500' : ''}`}
                      style={{ 
                        background: designType === type.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <div className="text-3xl mb-2">{type.icon}</div>
                      <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{type.name}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Templates */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Quick Start Templates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {industryConfig.templates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="p-4 rounded-xl text-left transition hover:ring-2 hover:ring-purple-500"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                    >
                      <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{template.name}</div>
                      <div className="text-lg font-bold mt-1" style={{ color: 'var(--accent-from)' }}>{template.title}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{template.subtitle}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-2 rounded-lg font-medium text-white transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Enter Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Design Details</h3>
              
              {isMenu ? (
                <div className="space-y-5" data-testid="menu-builder">
                  {/* Tip banner */}
                  <div className="p-3 rounded-lg text-xs" style={{ background: 'rgba(139,92,246,0.12)', color: 'var(--text-secondary)', border: '1px solid rgba(139,92,246,0.3)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>💡 Multi-Page Menu:</strong> Add a tab below for each page (e.g., 4 pages = 4 tabs). Fill the section name and items for each page, set your style/border/colors once in Step 3, then either <strong>Generate this Page</strong> (one at a time, review each) or <strong>Generate All Pages</strong> (one shot — same visual style across all pages).
                    <br /><span style={{ color: '#22c55e' }}>🔒 Brand-Lock:</span> Page 1 is automatically used as a visual reference for Pages 2+ so all pages share the exact same colors, fonts, borders and theme.
                  </div>

                  {/* Languages */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Menu Language(s)</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'en', label: 'English' },
                        { id: 'ar', label: 'العربية (Arabic)' }
                      ].map(l => (
                        <button
                          key={l.id}
                          data-testid={`menu-lang-${l.id}`}
                          onClick={() => toggleMenuLanguage(l.id)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition ${menuLanguages.includes(l.id) ? 'bg-purple-600 text-white' : ''}`}
                          style={!menuLanguages.includes(l.id) ? { background: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' } : {}}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                    {isBilingual && (
                      <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>Bilingual mode — fill in both English and Arabic for each item below. Arabic will be rendered Right-To-Left.</p>
                    )}
                  </div>

                  {/* Render Mode Toggle */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Rendering Style
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <button
                        data-testid="menu-mode-artistic"
                        onClick={() => setMenuRenderMode('artistic')}
                        className={`text-left p-3 rounded-lg transition ${menuRenderMode === 'artistic' ? 'ring-2 ring-purple-500' : ''}`}
                        style={{
                          background: menuRenderMode === 'artistic' ? 'rgba(139,92,246,0.2)' : 'var(--bg-tertiary)',
                          border: '1px solid var(--border-color)'
                        }}
                      >
                        <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>🎨 Artistic (recommended)</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Rich AI typography, elegant flourishes. Small risk of item duplication.</div>
                      </button>
                      <button
                        data-testid="menu-mode-precision"
                        onClick={() => setMenuRenderMode('precision')}
                        className={`text-left p-3 rounded-lg transition ${menuRenderMode === 'precision' ? 'ring-2 ring-purple-500' : ''}`}
                        style={{
                          background: menuRenderMode === 'precision' ? 'rgba(139,92,246,0.2)' : 'var(--bg-tertiary)',
                          border: '1px solid var(--border-color)'
                        }}
                      >
                        <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>🔒 Precision</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Guaranteed perfect text & no duplicates. Cleaner, less artistic body.</div>
                      </button>
                    </div>
                  </div>

                  {/* Restaurant Info (applies to every page) */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Restaurant Info <span className="text-xs" style={{ color: 'var(--text-muted)' }}>(shown on every page footer)</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        data-testid="menu-tagline"
                        type="text"
                        value={menuTagline}
                        onChange={(e) => setMenuTagline(e.target.value)}
                        placeholder="Tagline (e.g., Authentic Arabian Cuisine)"
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                      />
                      <input
                        data-testid="menu-contact"
                        type="text"
                        value={menuContact}
                        onChange={(e) => setMenuContact(e.target.value)}
                        placeholder="Contact (e.g., +971 50 123 4567)"
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                      />
                      <input
                        data-testid="menu-location"
                        type="text"
                        value={menuLocation}
                        onChange={(e) => setMenuLocation(e.target.value)}
                        placeholder="Location (e.g., Dubai Marina, UAE)"
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                      />
                    </div>
                  </div>

                  {/* Page Tabs */}
                  <div>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                        Pages ({menuTotalPages}) — currently editing <span style={{ color: '#8B5CF6' }}>Page {menuPageNumber}</span>
                      </label>
                      <button
                        data-testid="add-page-btn"
                        onClick={addPage}
                        className="px-3 py-1 rounded-lg text-xs font-medium text-white"
                        style={{ background: '#8B5CF6' }}
                      >
                        + Add Page
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {menuPages.map((p, idx) => {
                        const isActive = idx === activePageIdx;
                        const isGenerated = generatedPages.some(g => g.pageNumber === idx + 1);
                        return (
                          <div
                            key={idx}
                            data-testid={`page-tab-${idx}`}
                            className="flex items-center gap-1 rounded-lg overflow-hidden transition"
                            style={{
                              background: isActive ? 'rgba(139,92,246,0.25)' : 'var(--bg-tertiary)',
                              border: isActive ? '1px solid #8B5CF6' : '1px solid var(--border-color)'
                            }}
                          >
                            <button
                              onClick={() => setActivePageIdx(idx)}
                              className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {isGenerated && <span className="text-green-400">✓</span>}
                              Page {idx + 1}
                              {p.section && <span className="opacity-60">· {p.section}</span>}
                            </button>
                            <button
                              onClick={() => duplicatePage(idx)}
                              className="px-2 py-1.5 text-xs hover:bg-white/10"
                              title="Duplicate page"
                              style={{ color: 'var(--text-muted)' }}
                            >⎘</button>
                            {menuPages.length > 1 && (
                              <button
                                onClick={() => removePage(idx)}
                                className="px-2 py-1.5 text-xs text-red-400 hover:bg-red-500/10"
                                title="Delete page"
                              >✕</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                      Each page has its own section name and items. Click a tab to edit that page. The page number and total ({menuPageNumber} of {menuTotalPages}) are sent to the AI automatically.
                    </p>
                  </div>

                  {/* Section name for active page */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Section / Category for Page {menuPageNumber}
                    </label>
                    <input
                      data-testid="menu-section-input"
                      type="text"
                      value={menuSection}
                      onChange={(e) => setMenuSection(e.target.value)}
                      placeholder="e.g., Appetizers, Main Course, Desserts, Drinks"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>

                  {/* Menu Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Menu Items</label>
                      <div className="flex gap-2">
                        <button
                          data-testid="bulk-paste-toggle"
                          onClick={() => setBulkPasteMode(!bulkPasteMode)}
                          className="px-3 py-1 rounded-lg text-xs font-medium transition"
                          style={{ background: bulkPasteMode ? 'var(--bg-secondary)' : 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }}
                        >
                          {bulkPasteMode ? '✕ Close Paste' : '📋 Bulk Paste'}
                        </button>
                        <button
                          data-testid="add-menu-item"
                          onClick={addMenuItem}
                          className="px-3 py-1 rounded-lg text-xs font-medium text-white"
                          style={{ background: '#8B5CF6' }}
                        >
                          + Add Item
                        </button>
                      </div>
                    </div>

                    {bulkPasteMode && (
                      <div className="mb-3 p-3 rounded-lg space-y-2" style={{ background: 'var(--bg-tertiary)', border: '1px solid rgba(139,92,246,0.4)' }}>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          <strong style={{ color: 'var(--text-primary)' }}>Format:</strong> One item per line. Use <code style={{ background: 'var(--bg-secondary)', padding: '1px 4px', borderRadius: 3 }}>|</code> to separate <strong>Name | Description | Price</strong>.
                          {isBilingual && (
                            <> For bilingual: <code style={{ background: 'var(--bg-secondary)', padding: '1px 4px', borderRadius: 3 }}>English / Arabic</code> inside name and description.</>
                          )}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          <strong>Example:</strong>
                          <pre className="mt-1 p-2 rounded" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'pre-wrap' }}>
{isBilingual
? `Hummus / حمص | Creamy chickpea dip / حمص كريمي | $8.99
Falafel / فلافل | Crispy chickpea balls / كرات الحمص المقرمشة | $10.50
Tabbouleh / تبولة | Fresh parsley salad / سلطة البقدونس | $7.99`
: isArabicOnly
? `حمص | حمص كريمي مع زيت الزيتون | $8.99
فلافل | كرات الحمص المقرمشة | $10.50
تبولة | سلطة البقدونس الطازجة | $7.99`
: `Hummus | Creamy chickpea dip with olive oil | $8.99
Falafel | Crispy fried chickpea balls | $10.50
Tabbouleh | Fresh parsley salad with bulgur | $7.99`}
                          </pre>
                        </div>
                        <textarea
                          data-testid="bulk-paste-textarea"
                          value={bulkPasteText}
                          onChange={(e) => setBulkPasteText(e.target.value)}
                          placeholder="Paste your menu items here, one per line..."
                          rows={8}
                          dir={isArabicOnly ? 'rtl' : 'ltr'}
                          className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => { setBulkPasteText(''); setBulkPasteMode(false); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
                          >
                            Cancel
                          </button>
                          <button
                            data-testid="bulk-paste-parse"
                            onClick={parseBulkPaste}
                            disabled={!bulkPasteText.trim()}
                            className="px-4 py-1.5 rounded-lg text-xs font-medium text-white"
                            style={{ background: bulkPasteText.trim() ? '#8B5CF6' : '#555' }}
                          >
                            Parse & Add Items
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {menuItems.map((item, idx) => (
                        <div
                          key={idx}
                          data-testid={`menu-item-${idx}`}
                          className="p-3 rounded-lg space-y-2"
                          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Item {idx + 1}</span>
                            {menuItems.length > 1 && (
                              <button
                                data-testid={`remove-menu-item-${idx}`}
                                onClick={() => removeMenuItem(idx)}
                                className="text-xs text-red-400 hover:text-red-300"
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>

                          {/* Names row */}
                          <div className={`grid gap-2 ${isBilingual ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                            {!isArabicOnly && (
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => updateMenuItem(idx, 'name', e.target.value)}
                                placeholder="Dish name (English)"
                                className="px-3 py-2 rounded-lg text-sm"
                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                              />
                            )}
                            {(isArabicOnly || isBilingual) && (
                              <input
                                type="text"
                                dir="rtl"
                                value={item.name_ar}
                                onChange={(e) => updateMenuItem(idx, 'name_ar', e.target.value)}
                                placeholder="اسم الطبق"
                                className="px-3 py-2 rounded-lg text-sm"
                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                              />
                            )}
                          </div>

                          {/* Description row */}
                          <div className={`grid gap-2 ${isBilingual ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                            {!isArabicOnly && (
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateMenuItem(idx, 'description', e.target.value)}
                                placeholder="Description (English) — optional"
                                className="px-3 py-2 rounded-lg text-sm"
                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                              />
                            )}
                            {(isArabicOnly || isBilingual) && (
                              <input
                                type="text"
                                dir="rtl"
                                value={item.description_ar}
                                onChange={(e) => updateMenuItem(idx, 'description_ar', e.target.value)}
                                placeholder="الوصف — اختياري"
                                className="px-3 py-2 rounded-lg text-sm"
                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                              />
                            )}
                          </div>

                          {/* Price */}
                          <input
                            type="text"
                            value={item.price}
                            onChange={(e) => updateMenuItem(idx, 'price', e.target.value)}
                            placeholder="Price (e.g., $12.99 or 45 AED)"
                            className="w-full md:w-1/3 px-3 py-2 rounded-lg text-sm"
                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional extra design notes */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Visual Style Notes (optional)
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Optional: e.g., warm earthy tones, rustic wooden background, elegant gold accents..."
                      rows={2}
                      className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>
              ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Describe your {industry} design *
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={`E.g., Professional ${industry.toLowerCase()} promotional banner with modern design elements...`}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg text-sm"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Main Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="E.g., YOUR HEADLINE HERE"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Subtitle / Tagline</label>
                    <input
                      type="text"
                      value={subtitle}
                      onChange={(e) => setSubtitle(e.target.value)}
                      placeholder="E.g., Your compelling message"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Call-to-Action</label>
                    <input
                      type="text"
                      value={ctaText}
                      onChange={(e) => setCtaText(e.target.value)}
                      placeholder="E.g., Learn More"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Date (optional)</label>
                    <input
                      type="text"
                      value={dateText}
                      onChange={(e) => setDateText(e.target.value)}
                      placeholder="E.g., January 18-19, 2026"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Location (optional)</label>
                    <input
                      type="text"
                      value={locationText}
                      onChange={(e) => setLocationText(e.target.value)}
                      placeholder="E.g., Convention Center"
                      className="w-full px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>
              </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-2 rounded-lg font-medium transition"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="px-6 py-2 rounded-lg font-medium text-white transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Style & Generate */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Style & Options</h3>
              
              {/* Style Selection */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Design Style</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {DESIGN_STYLES.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-lg text-center transition ${style === s.id ? 'ring-2 ring-purple-500' : ''}`}
                      style={{ 
                        background: style === s.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{s.name}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Scheme */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Color Scheme</label>
                <div className="grid grid-cols-4 gap-3">
                  {COLOR_SCHEMES.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setColorScheme(c.id)}
                      className={`p-3 rounded-lg text-center transition ${colorScheme === c.id ? 'ring-2 ring-purple-500' : ''}`}
                      style={{ border: '1px solid var(--border-color)' }}
                    >
                      <div 
                        className="w-full h-8 rounded-md mb-2"
                        style={{ background: c.color }}
                      />
                      <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{c.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspect Ratio / Size */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Size / Aspect Ratio</label>
                
                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {['general', 'instagram', 'facebook', 'tiktok', 'linkedin', 'twitter', 'youtube', 'display', 'print'].map(cat => {
                    const catLabels = { display: 'Display Ads', print: 'Large Format' };
                    const isActive = !showCustomSize && ASPECT_RATIOS.find(ar => ar.id === aspectRatio)?.category === cat;
                    return (
                      <button
                        key={cat}
                        data-testid={`size-cat-${cat}`}
                        onClick={() => {
                          setShowCustomSize(false);
                          const firstInCategory = ASPECT_RATIOS.find(ar => ar.category === cat);
                          if (firstInCategory) setAspectRatio(firstInCategory.id);
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition ${isActive ? 'bg-purple-500 text-white' : ''}`}
                        style={!isActive ? { background: 'var(--bg-tertiary)', color: 'var(--text-muted)' } : {}}
                      >
                        {catLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    );
                  })}
                  <button
                    data-testid="size-cat-custom"
                    onClick={() => setShowCustomSize(true)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${showCustomSize ? 'bg-purple-500 text-white' : ''}`}
                    style={!showCustomSize ? { background: 'var(--bg-tertiary)', color: 'var(--text-muted)' } : {}}
                  >
                    Custom Size
                  </button>
                </div>
                
                {showCustomSize ? (
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <select
                        data-testid="custom-unit-select"
                        value={customUnit}
                        onChange={(e) => setCustomUnit(e.target.value)}
                        className="px-3 py-1.5 rounded-lg text-sm"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                      >
                        <option value="inches">Inches</option>
                        <option value="feet">Feet</option>
                        <option value="cm">Centimeters</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Width</label>
                        <input
                          data-testid="custom-width-input"
                          type="number"
                          min="1"
                          value={customWidth}
                          onChange={(e) => setCustomWidth(e.target.value)}
                          placeholder={customUnit === 'feet' ? 'e.g. 5' : customUnit === 'cm' ? 'e.g. 150' : 'e.g. 60'}
                          className="w-full px-3 py-2 rounded-lg text-sm"
                          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                        />
                      </div>
                      <span className="mt-5 text-lg font-bold" style={{ color: 'var(--text-muted)' }}>×</span>
                      <div className="flex-1">
                        <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Height</label>
                        <input
                          data-testid="custom-height-input"
                          type="number"
                          min="1"
                          value={customHeight}
                          onChange={(e) => setCustomHeight(e.target.value)}
                          placeholder={customUnit === 'feet' ? 'e.g. 8' : customUnit === 'cm' ? 'e.g. 200' : 'e.g. 80'}
                          className="w-full px-3 py-2 rounded-lg text-sm"
                          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                        />
                      </div>
                    </div>
                    {(() => {
                      const px = getCustomPixelSize();
                      if (px) {
                        let dispW = customWidth, dispH = customHeight, unit = customUnit;
                        if (unit === 'feet') { dispW = (parseFloat(customWidth) * 12).toFixed(1); dispH = (parseFloat(customHeight) * 12).toFixed(1); unit = 'inches (converted)'; }
                        if (unit === 'cm') { dispW = (parseFloat(customWidth) / 2.54).toFixed(1); dispH = (parseFloat(customHeight) / 2.54).toFixed(1); unit = 'inches (converted)'; }
                        return (
                          <div className="mt-3 p-2 rounded-lg text-xs" style={{ background: 'rgba(139,92,246,0.1)', color: 'var(--text-secondary)' }}>
                            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>Canvas: {px.width} × {px.height} px</span>
                            <span className="ml-2">(proportional workspace — export at 2x/3x for print quality)</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <>
                    {/* Size options for selected category */}
                    <div className="grid grid-cols-3 gap-2">
                      {ASPECT_RATIOS.filter(ar => ar.category === (ASPECT_RATIOS.find(a => a.id === aspectRatio)?.category || 'general')).map(ar => (
                        <button
                          key={ar.id}
                          data-testid={`size-option-${ar.id}`}
                          onClick={() => setAspectRatio(ar.id)}
                          className={`p-2 rounded-lg text-center transition ${aspectRatio === ar.id ? 'ring-2 ring-purple-500' : ''}`}
                          style={{ 
                            background: aspectRatio === ar.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          <div className="text-lg mb-1">{ar.icon}</div>
                          <div className="font-medium text-xs" style={{ color: 'var(--text-primary)' }}>{ar.name}</div>
                          <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{ar.inches || ar.size}</div>
                        </button>
                      ))}
                    </div>
                    {/* Print info notice */}
                    {ASPECT_RATIOS.find(a => a.id === aspectRatio)?.category === 'print' && (
                      <div className="mt-2 p-2 rounded-lg text-xs" style={{ background: 'rgba(139,92,246,0.1)', color: 'var(--text-secondary)' }}>
                        Designs are created at proportional workspace size. Export at 2x/3x from the Image Editor for print-quality resolution.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Border Style */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Border Style</label>
                <div className="grid grid-cols-4 gap-2">
                  {BORDER_STYLES.map(b => (
                    <button
                      key={b.id}
                      data-testid={`border-${b.id}`}
                      onClick={() => setBorderStyle(b.id)}
                      className={`p-2 rounded-lg text-center transition ${borderStyle === b.id ? 'ring-2 ring-purple-500' : ''}`}
                      style={{
                        background: borderStyle === b.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <div className="text-lg">{b.icon}</div>
                      <div className="font-medium text-xs mt-1" style={{ color: 'var(--text-primary)' }}>{b.name}</div>
                    </button>
                  ))}
                </div>
                {borderStyle !== 'none' && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Border Color:</label>
                    <div className="flex gap-1">
                      {[
                        brandSettings?.brand_primary_color,
                        brandSettings?.brand_secondary_color,
                        '#FFFFFF', '#000000', '#D4AF37', '#C0C0C0'
                      ].filter(Boolean).map(c => (
                        <button
                          key={c}
                          onClick={() => setBorderColor(c)}
                          className={`w-7 h-7 rounded-full transition ${borderColor === c ? 'ring-2 ring-purple-500 ring-offset-2' : ''}`}
                          style={{ background: c, border: '1px solid var(--border-color)', ringOffsetColor: 'var(--bg-secondary)' }}
                        />
                      ))}
                      <input
                        type="color"
                        value={borderColor || '#D4AF37'}
                        onChange={(e) => setBorderColor(e.target.value)}
                        className="w-7 h-7 rounded cursor-pointer"
                        style={{ border: '1px solid var(--border-color)' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Design Presets */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Design Presets</label>
                  <button
                    data-testid="save-preset-btn"
                    onClick={() => setShowPresetPanel(!showPresetPanel)}
                    className="px-3 py-1 rounded-lg text-xs font-medium transition"
                    style={{ background: 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }}
                  >
                    {showPresetPanel ? 'Cancel' : '+ Save Current'}
                  </button>
                </div>
                {showPresetPanel && (
                  <div className="flex gap-2 mb-3">
                    <input
                      data-testid="preset-name-input"
                      type="text"
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      placeholder="Preset name (e.g., Dark Sale Posts)"
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                    />
                    <button
                      data-testid="confirm-save-preset"
                      onClick={savePreset}
                      disabled={!presetName.trim()}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-white"
                      style={{ background: presetName.trim() ? '#8B5CF6' : '#555' }}
                    >
                      Save
                    </button>
                  </div>
                )}
                {presets.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {presets.map(p => (
                      <div key={p.id} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                        <button
                          data-testid={`load-preset-${p.id}`}
                          onClick={() => loadPreset(p)}
                          className="font-medium hover:underline"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {p.name}
                        </button>
                        <button
                          onClick={() => deletePreset(p.id)}
                          className="ml-1 text-red-400 hover:text-red-300"
                          style={{ fontSize: '10px' }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No presets saved. Set your preferred style, colors & border, then click "Save Current" to reuse across designs.</p>
                )}
              </div>

              {/* Include Brand Name Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={includeLogo}
                  onChange={(e) => setIncludeLogo(e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Include brand name in design</span>
              </div>

              {/* Brand Colors Preview */}
              {brandSettings?.brand_primary_color && colorScheme === 'brand' && (
                <div className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Brand Colors from Settings:</span>
                  </div>
                  <div className="flex gap-2">
                    {[brandSettings.brand_primary_color, brandSettings.brand_secondary_color, brandSettings.brand_accent_color_1, brandSettings.brand_accent_color_2].filter(Boolean).map((c, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <div className="w-6 h-6 rounded-full" style={{ background: c, border: '1px solid var(--border-color)' }} />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-between flex-wrap gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-2 rounded-lg font-medium transition"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  ← Back
                </button>
                <div className="flex gap-2 flex-wrap">
                  {isMenu && menuPages.length > 1 && (
                    <button
                      data-testid="generate-all-pages-btn"
                      onClick={generateAllPages}
                      disabled={isGenerating || bulkGenerating}
                      className="px-6 py-3 rounded-lg font-medium text-white transition hover:opacity-90 flex items-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', opacity: (isGenerating || bulkGenerating) ? 0.7 : 1 }}
                    >
                      {bulkGenerating ? (
                        <>
                          <span className="animate-spin">⚙️</span>
                          Generating {bulkProgress.current}/{bulkProgress.total}...
                        </>
                      ) : (
                        <>⚡ Generate All {menuPages.length} Pages</>
                      )}
                    </button>
                  )}
                  <button
                    data-testid="generate-design-btn"
                    onClick={generateDesign}
                    disabled={isGenerating || bulkGenerating}
                    className="px-8 py-3 rounded-lg font-medium text-white transition hover:opacity-90 flex items-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)', opacity: (isGenerating || bulkGenerating) ? 0.7 : 1 }}
                  >
                    {isGenerating ? (
                      <>
                        <span className="animate-spin">⚙️</span>
                        Generating...
                      </>
                    ) : (
                      <>🎨 {isMenu ? `Generate Page ${menuPageNumber}` : 'Generate Design'}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && generatedImage && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {isMenu && generatedPages.length > 1
                  ? `Generated Menu (${generatedPages.length} pages)`
                  : `Generated Design for ${industry}`}
              </h3>

              {isMenu && generatedPages.length > 1 ? (
                <>
                  {/* Multi-page gallery */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="menu-gallery">
                    {generatedPages.map(p => (
                      <div
                        key={p.pageNumber}
                        className="rounded-xl overflow-hidden flex flex-col"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                      >
                        <img
                          src={p.image}
                          alt={`Page ${p.pageNumber}`}
                          className="w-full aspect-[3/4] object-cover cursor-pointer"
                          onClick={() => setGeneratedImage(p.image)}
                        />
                        <div className="p-2">
                          <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                            Page {p.pageNumber}{p.section ? ' · ' + p.section : ''}
                          </div>
                          <div className="flex gap-1 mt-1">
                            {[1, 2, 3].map(s => (
                              <button
                                key={s}
                                onClick={() => downloadGeneratedPage(p, s)}
                                className="flex-1 px-1 py-1 rounded text-xs font-medium transition"
                                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
                                title={`Download at ${s}x resolution`}
                              >
                                {s}x
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Active large preview */}
                  <div className="flex justify-center">
                    <img
                      src={generatedImage}
                      alt="Selected page"
                      className="max-w-full max-h-[400px] rounded-xl shadow-lg"
                      style={{ border: '1px solid var(--border-color)' }}
                    />
                  </div>

                  <div className="flex justify-center gap-3 flex-wrap">
                    <button
                      onClick={() => { setGeneratedImage(null); setGeneratedPages([]); setStep(3); }}
                      className="px-6 py-2 rounded-lg font-medium transition"
                      style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                    >
                      ← Edit & Regenerate
                    </button>
                    <button
                      data-testid="download-all-pages-btn"
                      onClick={() => downloadAllPages(downloadScale)}
                      className="px-6 py-2 rounded-lg font-medium text-white transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                    >
                      ⬇ Download All Pages ({downloadScale}x)
                    </button>
                    <div className="flex items-center gap-1 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                      {[1, 2, 3].map(s => (
                        <button
                          key={s}
                          onClick={() => setDownloadScale(s)}
                          className={`px-3 py-2 text-xs font-medium transition ${downloadScale === s ? 'bg-purple-600 text-white' : ''}`}
                          style={downloadScale !== s ? { color: 'var(--text-secondary)' } : {}}
                        >
                          {s}x
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setStep(5)}
                      className="px-8 py-3 rounded-lg font-medium text-white transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                    >
                      Continue with Selected Page →
                    </button>
                  </div>
                  <p className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                    Click any thumbnail to set it as the active page for AI captions / posting in the next step.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-center">
                    <img 
                      src={generatedImage} 
                      alt="Generated Design"
                      className="max-w-full max-h-[400px] rounded-xl shadow-lg"
                      style={{ border: '1px solid var(--border-color)' }}
                    />
                  </div>

                  <div className="flex justify-center gap-4 flex-wrap">
                    <button
                      onClick={() => {
                        setGeneratedImage(null);
                        setStep(3);
                      }}
                      className="px-6 py-2 rounded-lg font-medium transition"
                      style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                    >
                      Regenerate
                    </button>
                    
                    {/* High-Res Download */}
                    <div className="flex items-center gap-1 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                      {[1,2,3].map(s => (
                        <button
                          key={s}
                          data-testid={`download-scale-${s}x`}
                          onClick={() => setDownloadScale(s)}
                          className={`px-3 py-2 text-xs font-medium transition ${downloadScale === s ? 'bg-purple-600 text-white' : ''}`}
                          style={downloadScale !== s ? { color: 'var(--text-secondary)' } : {}}
                        >
                          {s}x
                        </button>
                      ))}
                      <button
                        data-testid="download-highres-btn"
                        onClick={downloadHighRes}
                        className="px-4 py-2 rounded-r-lg font-medium text-sm transition flex items-center gap-1"
                        style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderLeft: '1px solid var(--border-color)' }}
                      >
                        Download {downloadScale}x
                      </button>
                    </div>
                    
                    <button
                      onClick={() => setStep(5)}
                      className="px-8 py-3 rounded-lg font-medium text-white transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                    >
                      Use This Design
                    </button>
                  </div>
                  
                  {/* Resolution info */}
                  <div className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                    Download at {downloadScale}x for {downloadScale === 1 ? 'web/screen use' : downloadScale === 2 ? 'high-quality print' : 'maximum print quality (large format)'}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: AI Content */}
          {step === 5 && generatedImage && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>✍️ Write AI Content</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Design Preview */}
                <div>
                  <img 
                    src={generatedImage} 
                    alt="Generated Design"
                    className="w-full rounded-xl shadow-lg"
                    style={{ border: '1px solid var(--border-color)' }}
                  />
                </div>
                
                {/* Content Form */}
                <div className="space-y-4">
                  {/* Platform Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Select Platform</label>
                    <div className="grid grid-cols-5 gap-2">
                      {PLATFORMS.map(platform => (
                        <button
                          key={platform.id}
                          onClick={() => setSelectedPlatform(platform.id)}
                          className={`p-2 rounded-lg text-center transition ${selectedPlatform === platform.id ? 'ring-2 ring-purple-500' : ''}`}
                          style={{ 
                            background: selectedPlatform === platform.id ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          <div className="text-xl">{platform.icon}</div>
                          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{platform.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Generate AI Content Button */}
                  <button
                    onClick={generateAIContent}
                    disabled={isGeneratingContent}
                    className="w-full py-3 rounded-lg font-medium text-white transition hover:opacity-90 flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)', opacity: isGeneratingContent ? 0.7 : 1 }}
                  >
                    {isGeneratingContent ? (
                      <>
                        <span className="animate-spin">⚙️</span>
                        Generating Content...
                      </>
                    ) : (
                      <>🤖 Generate AI Caption & Hashtags</>
                    )}
                  </button>

                  {/* Caption */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Caption</label>
                    <textarea
                      value={aiCaption}
                      onChange={(e) => setAiCaption(e.target.value)}
                      placeholder="Your caption will appear here..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>

                  {/* Hashtags */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Hashtags</label>
                    <textarea
                      value={aiHashtags}
                      onChange={(e) => setAiHashtags(e.target.value)}
                      placeholder="#hashtags will appear here..."
                      rows={2}
                      className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(4)}
                  className="px-6 py-2 rounded-lg font-medium transition"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  ← Back
                </button>
                <button
                  onClick={saveAsPost}
                  disabled={isSavingPost || !aiCaption.trim()}
                  className="px-8 py-3 rounded-lg font-medium text-white transition hover:opacity-90 flex items-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', opacity: (isSavingPost || !aiCaption.trim()) ? 0.7 : 1 }}
                >
                  {isSavingPost ? (
                    <>
                      <span className="animate-spin">⚙️</span>
                      Saving...
                    </>
                  ) : (
                    <>💾 Save as Post</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Post Saved */}
          {step === 6 && postSaved && (
            <div className="space-y-6 text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Post Saved Successfully!</h3>
              <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
                Your {industry} design has been saved as a draft post
              </p>
              
              <div className="flex justify-center">
                <img 
                  src={generatedImage} 
                  alt="Generated Design"
                  className="max-w-xs rounded-xl shadow-lg"
                  style={{ border: '1px solid var(--border-color)' }}
                />
              </div>

              <div className="p-4 rounded-xl max-w-lg mx-auto" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                <p className="text-sm mb-2" style={{ color: 'var(--text-primary)' }}>{aiCaption}</p>
                <p className="text-sm" style={{ color: 'var(--accent-from)' }}>{aiHashtags}</p>
              </div>

              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => {
                    // Reset and create new design
                    setStep(1);
                    setGeneratedImage(null);
                    setGeneratedPages([]);
                    setAiCaption('');
                    setAiHashtags('');
                    setPostSaved(false);
                    setPrompt('');
                    setTitle('');
                    setSubtitle('');
                    setCtaText('');
                    setMenuPages([{ section: '', items: [{ name: '', name_ar: '', description: '', description_ar: '', price: '' }] }]);
                    setActivePageIdx(0);
                  }}
                  className="px-6 py-2 rounded-lg font-medium transition"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  🎨 Create Another Design
                </button>
                <button
                  onClick={onClose}
                  className="px-8 py-3 rounded-lg font-medium text-white transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)' }}
                >
                  ✓ Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIDesignGenerator;
